import React from "react";
import { useTranslation } from "react-i18next";

import { Button, Tooltip } from "antd";
import { FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";

import { API_URL } from "../../shared/utils/Util";
import { useNotification } from "../../shared/hooks";
import { downloadFile, slugify } from "./download";

type TableSignButtonProps = {
  restaurantId: string;
  table_id: string;
  table_name: string;
};

// TableSignButton downloads a single table's print-ready branded sign (PDF).
export const TableSignButton = (props: TableSignButtonProps) => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [loading, setLoading] = React.useState(false);

  const onClick = async () => {
    setLoading(true);
    try {
      await downloadFile(
        `${API_URL}/v1/r/${props.restaurantId}/tables/${props.table_id}/sign`,
        `${slugify(props.table_name)}_sign.pdf`,
        "application/pdf"
      );
    } catch (err) {
      showError(t("failed_to_download_sign"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={t("download_sign")}>
      <Button shape="circle" icon={<FilePdfOutlined />} loading={loading} onClick={onClick} />
    </Tooltip>
  );
};

type DownloadSignsButtonProps = {
  restaurantId: string;
  // selectedIds are the table IDs the user has checked. When empty, the button
  // downloads every table's sign; otherwise just the selected ones.
  selectedIds: string[];
  disabled?: boolean;
};

// DownloadSignsButton downloads a print-ready, two-up PDF sheet of table signs:
// all tables when nothing is selected, or the selected subset when some rows
// are checked.
export const DownloadSignsButton = (props: DownloadSignsButtonProps) => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [loading, setLoading] = React.useState(false);

  const hasSelection = props.selectedIds.length > 0;

  const onClick = async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/v1/r/${props.restaurantId}/tables/signs`;
      await downloadFile(
        url,
        "table-signs.pdf",
        "application/pdf",
        hasSelection ? { table_ids: props.selectedIds } : undefined
      );
    } catch (err) {
      showError(t("failed_to_download_sign"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      icon={<PrinterOutlined />}
      loading={loading}
      disabled={props.disabled}
      onClick={onClick}
    >
      {hasSelection
        ? t("download_selected_signs", { count: props.selectedIds.length })
        : t("download_all_signs")}
    </Button>
  );
};
